import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContacts, fetchPipelineStages } from '../api/crm';
import type { Contact, PipelineStage } from '../types/crm';
import { formatDateDDMMYYYY } from '../utils/date';

export default function CrmArchivePage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([fetchContacts(true), fetchPipelineStages()])
      .then(([c, s]) => {
        setContacts(c);
        setStages(s);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const stageById = useMemo(
    () => new Map(stages.map((s) => [s.id, s])),
    [stages]
  );

  if (loading) return <div>Loading…</div>;
  if (error) return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;

  return (
    <>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)', fontWeight: 600, marginBottom: '8px' }}>CRM</p>
      <h1 style={{ fontSize: '36px', fontWeight: 700, margin: '0 0 24px' }}>Archive</h1>

      {contacts.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
          No archived contacts yet.
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-warm-bg)' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Company</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Stage</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Job Title</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Outreach</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Last contacted</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Archived date</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => {
                const stage = stageById.get(c.stage_id);
                return (
                  <tr
                    key={c.id}
                    style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/contact/${c.id}`)}
                  >
                    <td style={{ padding: '12px 16px' }}>{c.first_name} {c.last_name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.company ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }}>{stage?.name ?? '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.job_title ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }}>{c.outreach_type}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
                      {c.last_contacted_at ? formatDateDDMMYYYY(c.last_contacted_at) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
                      {c.archived_at ? formatDateDDMMYYYY(c.archived_at) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
